import { createFileRoute, useNavigate, retainSearchParams } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listJobsAdmin, getJobDetail, retryJob, cancelJob, bulkRequeueDeadJobs } from "@/lib/integrations.functions";
import { runServerFn } from "@/lib/server-fn";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { DataTable, type ColumnDef } from "@/components/ui/data-table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { FilterBar } from "@/components/ui/filter-bar";
import { PageHeader } from "@/components/ui/page-header";
import { RotateCw, X, Eye, Loader2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { DateRangeFilter } from "@/components/filters/date-range-filter";
import { SortDirectionToggle } from "@/components/filters/sort-direction-toggle";
import { dateRangeSearchSchema, dateRangeToBounds, retainDateRangeKeys } from "@/lib/date-range-search";

export const Route = createFileRoute("/_authenticated/app/admin/jobs")({
  validateSearch: zodValidator(dateRangeSearchSchema),
  search: { middlewares: [retainSearchParams([...retainDateRangeKeys])] },
  component: JobsAdmin,
});

type Job = {
  id: string; connection_id: string; tenant_id: string;
  kind: string; status: "queued" | "running" | "succeeded" | "dead";
  attempts: number; max_attempts: number;
  run_at: string; finished_at: string | null; error: string | null; created_at: string;
  connection_label: string | null; connection_provider: string | null;
};

const STATUS_COLORS: Record<Job["status"], string> = {
  queued: "bg-blue-500/10 text-blue-700 border-blue-200",
  running: "bg-warning/10 text-warning border-warning/30",
  succeeded: "bg-success/10 text-success border-success/30",
  dead: "bg-destructive/10 text-destructive border-destructive/30",
};

function JobsAdmin() {
  const list = useServerFn(listJobsAdmin);
  const detail = useServerFn(getJobDetail);
  const retryFn = useServerFn(retryJob);
  const cancelFn = useServerFn(cancelJob);
  const bulkFn = useServerFn(bulkRequeueDeadJobs);
  const qc = useQueryClient();
  const [status, setStatus] = useState<string>("");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const urlSearch = Route.useSearch();
  type S = typeof urlSearch;
  const navigate = useNavigate({ from: Route.fullPath });
  const update = (patch: Partial<S>) => navigate({ search: (prev: S) => ({ ...prev, ...patch }) });
  const sort = urlSearch.sort ?? "desc";
  // Default last 24h if user hasn't picked one
  const effectiveStart = urlSearch.start
    ?? (urlSearch.end ? undefined : new Date(Date.now() - 86400000).toISOString().slice(0, 10));
  const effectiveEnd = urlSearch.end;
  const { fromIso, toIso } = useMemo(
    () => dateRangeToBounds({ start: effectiveStart, end: effectiveEnd }),
    [effectiveStart, effectiveEnd],
  );

  const { data, isLoading } = useQuery({
    queryKey: ["admin-jobs", status, search, fromIso, toIso, sort],
    queryFn: () => list({ data: {
      status: (status || undefined) as Job["status"] | undefined,
      search: search.trim() || undefined,
      limit: 200,
      fromIso, toIso, sort,
    } }),
    refetchInterval: 5_000,
  });

  const { data: detailData } = useQuery({
    queryKey: ["admin-job-detail", openId],
    enabled: !!openId,
    queryFn: () => detail({ data: { jobId: openId! } }),
  });

  const onRetry = async (id: string) => {
    setActing(true);
    try {
      await runServerFn(() => retryFn({ data: { jobId: id } }), "Retry job");
      toast.success("Job re-queued");
      qc.invalidateQueries({ queryKey: ["admin-jobs"] });
    } finally { setActing(false); }
  };
  const onCancel = async (id: string) => {
    if (!confirm("Cancel this job?")) return;
    setActing(true);
    try {
      await runServerFn(() => cancelFn({ data: { jobId: id } }), "Cancel job");
      toast.success("Job cancelled");
      qc.invalidateQueries({ queryKey: ["admin-jobs"] });
    } finally { setActing(false); }
  };

  const onBulkRequeue = async () => {
    const dead = (data?.jobs ?? []).filter((j: Job) => j.status === "dead").slice(0, 100).map((j: Job) => j.id);
    if (dead.length === 0) { toast.info("No dead jobs to requeue"); return; }
    if (!confirm(`Requeue ${dead.length} dead job(s)?`)) return;
    setActing(true);
    try {
      const res = await runServerFn(() => bulkFn({ data: { jobIds: dead } }), "Bulk requeue dead jobs");
      toast.success(`Requeued ${res.ok}, failed ${res.failed}`);
      qc.invalidateQueries({ queryKey: ["admin-jobs"] });
    } finally { setActing(false); }
  };

  const cols: ColumnDef<Job, unknown>[] = [
    { id: "kind", header: "Kind", accessorKey: "kind",
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.kind}</span> },
    { id: "conn", header: "Connection",
      cell: ({ row }) => (
        <div className="text-sm">
          <div className="font-medium">{row.original.connection_label ?? "—"}</div>
          <div className="text-xs text-muted-foreground">{row.original.connection_provider ?? ""}</div>
        </div>
      ),
    },
    { id: "status", header: "Status",
      cell: ({ row }) => <Badge variant="outline" className={STATUS_COLORS[row.original.status]}>{row.original.status}</Badge> },
    { id: "attempts", header: "Attempts",
      cell: ({ row }) => <span className="tabular-nums text-sm">{row.original.attempts}/{row.original.max_attempts}</span> },
    { id: "run_at", header: "Run at",
      cell: ({ row }) => <span className="text-xs text-muted-foreground">{new Date(row.original.run_at).toLocaleString()}</span> },
    { id: "error", header: "Error",
      cell: ({ row }) => (
        <span className="text-xs text-destructive line-clamp-1 max-w-[28ch]" title={row.original.error ?? ""}>
          {row.original.error ?? ""}
        </span>
      ),
    },
    { id: "actions", header: "", enableSorting: false,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setOpenId(row.original.id); }}>
            <Eye className="h-4 w-4" />
          </Button>
          {(row.original.status === "dead" || row.original.status === "succeeded") && (
            <Button size="sm" variant="ghost" disabled={acting} onClick={(e) => { e.stopPropagation(); onRetry(row.original.id); }}>
              <RotateCw className="h-4 w-4" />
            </Button>
          )}
          {(row.original.status === "queued" || row.original.status === "running") && (
            <Button size="sm" variant="ghost" disabled={acting} onClick={(e) => { e.stopPropagation(); onCancel(row.original.id); }}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="Background Jobs" description="Integration queue: retry failures, cancel stuck jobs, inspect requests." />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Filter by error message…"
        >
          <Select value={status} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[160px] h-9"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="queued">Queued</SelectItem>
              <SelectItem value="running">Running</SelectItem>
              <SelectItem value="succeeded">Succeeded</SelectItem>
              <SelectItem value="dead">Dead</SelectItem>
            </SelectContent>
          </Select>
          <DateRangeFilter
            value={{ start: urlSearch.start, end: urlSearch.end }}
            onChange={(next) => update({ start: next.start, end: next.end })}
          />
          <SortDirectionToggle
            value={sort}
            onChange={(s) => update({ sort: s === "desc" ? undefined : s })}
          />
          {status === "dead" && (
            <Button size="sm" variant="outline" disabled={acting} onClick={onBulkRequeue}>
              <RefreshCcw className="h-4 w-4 mr-2" />Requeue all dead
            </Button>
          )}
        </FilterBar>
        <DataTable
          data={(data?.jobs ?? []) as Job[]}
          columns={cols}
          isLoading={isLoading}
          empty={{ title: "No jobs", description: "No queued or recent jobs match your filters." }}
        />
      </div>

      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent className="sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Job detail</SheetTitle>
            <SheetDescription>{detailData?.connection?.label} · {detailData?.connection?.provider}</SheetDescription>
          </SheetHeader>
          {!detailData ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="mt-6 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Field label="Kind"><span className="font-mono">{detailData.job.kind}</span></Field>
                <Field label="Status">
                  <Badge variant="outline" className={STATUS_COLORS[detailData.job.status as Job["status"]]}>
                    {detailData.job.status}
                  </Badge>
                </Field>
                <Field label="Attempts">{detailData.job.attempts}/{detailData.job.max_attempts}</Field>
                <Field label="Run at">{new Date(detailData.job.run_at).toLocaleString()}</Field>
                <Field label="Started">{detailData.job.started_at ? new Date(detailData.job.started_at).toLocaleString() : "—"}</Field>
                <Field label="Finished">{detailData.job.finished_at ? new Date(detailData.job.finished_at).toLocaleString() : "—"}</Field>
                <Field label="Idempotency key" full><span className="font-mono text-xs">{detailData.job.idempotency_key ?? "—"}</span></Field>
              </div>
              {detailData.job.error && (
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">Error</div>
                  <pre className="text-xs bg-red-50 border border-red-200 rounded p-2 overflow-auto whitespace-pre-wrap">{detailData.job.error}</pre>
                </div>
              )}
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">Payload</div>
                <pre className="text-xs bg-muted rounded p-2 overflow-auto max-h-60">{JSON.stringify(detailData.job.payload, null, 2)}</pre>
              </div>
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
                  Recent integration events ({detailData.events.length})
                </div>
                <div className="space-y-2">
                  {detailData.events.map((ev: any) => (
                    <details key={ev.id} className="border rounded p-2 text-xs">
                      <summary className="cursor-pointer flex items-center gap-2">
                        <Badge variant="outline">{ev.direction}</Badge>
                        <span className="font-mono">{ev.kind}</span>
                        {ev.http_status != null && <span>HTTP {ev.http_status}</span>}
                        {ev.duration_ms != null && <span className="text-muted-foreground">{ev.duration_ms}ms</span>}
                        <span className="ml-auto text-muted-foreground">{new Date(ev.created_at).toLocaleTimeString()}</span>
                      </summary>
                      {ev.error && <pre className="mt-2 text-destructive whitespace-pre-wrap">{ev.error}</pre>}
                      {ev.request && <pre className="mt-2 max-h-40 overflow-auto">{JSON.stringify(ev.request, null, 2)}</pre>}
                      {ev.response && <pre className="mt-2 max-h-40 overflow-auto bg-muted rounded p-1">{typeof ev.response === "string" ? ev.response : JSON.stringify(ev.response, null, 2)}</pre>}
                    </details>
                  ))}
                  {detailData.events.length === 0 && <p className="text-xs text-muted-foreground">No events recorded near this job.</p>}
                </div>
              </div>
              <div className="flex gap-2 pt-2 border-t">
                {(detailData.job.status === "dead" || detailData.job.status === "succeeded") && (
                  <Button variant="outline" disabled={acting} onClick={() => onRetry(detailData.job.id)}>
                    <RotateCw className="h-4 w-4 mr-2" />Retry
                  </Button>
                )}
                {(detailData.job.status === "queued" || detailData.job.status === "running") && (
                  <Button variant="outline" disabled={acting} onClick={() => onCancel(detailData.job.id)}>
                    <X className="h-4 w-4 mr-2" />Cancel
                  </Button>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

function Field({ label, children, full }: { label: string; children: React.ReactNode; full?: boolean }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}